import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { getDashboardStats, updateBattleStatus } from "../../api/dashboardapi";

// ── Icons ──────────────────────────────────────────────────────────────────────
const UserIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.24-8 5v1a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1c0-2.76-3.58-5-8-5Z" />
    </svg>
);

const QuestionIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 15.5h-2v-2h2v2Zm1.7-6.1-.9.9c-.5.5-.8.9-.8 1.7h-2v-.5c0-.8.3-1.5.9-2.1l1.2-1.3c.3-.3.5-.7.5-1.1 0-.9-.7-1.6-1.6-1.6s-1.6.7-1.6 1.6H8.4a3.6 3.6 0 1 1 7.2 0c0 .8-.3 1.5-.9 2.4Z" />
    </svg>
);

const ChatIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M4 3h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 1-2Z" />
    </svg>
);

const ImageIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm3.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM5 19h14v-3.5l-3.5-4.5-4 5-2.5-3L5 17v2Z" />
    </svg>
);

const BoltIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" />
    </svg>
);

const ArrowIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);

// ── Config ─────────────────────────────────────────────────────────────────────
// key  = getDashboardStats() ma return thata field
// path = te page no route (tamara router pramane badli lejo)
const SECTIONS = [
    {
        title: "Users",
        label: "Total Users",
        key: "totalUsers",
        path: "/",
        Icon: UserIcon,
        cardBg: "bg-[#EDE8FF] dark:bg-[#8B5CF6]/10",
        iconBg: "bg-[#8B5CF6]",
    },
    {
        title: "Battle Questions",
        label: "Total Battle Questions",
        key: "totalBattleQuestions",
        path: "/battle-que",
        Icon: QuestionIcon,
        cardBg: "bg-[#DDF9E7] dark:bg-[#3DD958]/10",
        iconBg: "bg-[#3DD958]",
    },
    {
        title: "Situations",
        label: "Total Situations",
        key: "totalSituations",
        path: "/situation",
        Icon: ChatIcon,
        cardBg: "bg-[#FFF2DC] dark:bg-[#FF8F73]/10",
        iconBg: "bg-[#FF8F73]",
    },
    {
        title: "Card Backgrounds",
        label: "Total Card Backgrounds",
        key: "totalCardBgs",
        path: "/cardbg",
        Icon: ImageIcon,
        cardBg: "bg-[#E0F2FE] dark:bg-[#38BDF8]/10",
        iconBg: "bg-[#38BDF8]",
    },
];

// ── Stat Card ──────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, path, Icon, cardBg, iconBg, loading }) => (
    <Link
        to={path}
        className={`group block rounded-2xl p-5 transition hover:-translate-y-0.5 hover:shadow-md ${cardBg}`}
    >
        <div className="flex items-start justify-between">
            <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${iconBg}`}
            >
                <Icon />
            </div>
            <span className="text-gray-500 opacity-0 transition group-hover:opacity-100 dark:text-gray-300">
                <ArrowIcon />
            </span>
        </div>

        <div className="mt-6 flex items-end justify-between gap-4">
            <p className="text-base text-gray-600 dark:text-gray-300">{label}</p>
            {loading ? (
                <div className="h-8 w-16 rounded bg-black/10 dark:bg-white/10 animate-pulse" />
            ) : (
                <p className="text-3xl font-bold text-gray-700 dark:text-white">
                    {Number(value ?? 0).toLocaleString()}
                </p>
            )}
        </div>
    </Link>
);

// ── Battle Status Card (toggle) ────────────────────────────────────────────────
const BattleStatusCard = ({ isBattle, loading, updating, onToggle }) => (
    <div
        className={`rounded-2xl p-5 transition ${
            isBattle
                ? "bg-[#DDF9E7] dark:bg-[#3DD958]/10"
                : "bg-gray-100 dark:bg-white/5"
        }`}
    >
        <div className="flex items-start justify-between">
            <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${
                    isBattle ? "bg-[#3DD958]" : "bg-gray-400"
                }`}
            >
                <BoltIcon />
            </div>

            {loading ? (
                <div className="h-6 w-11 rounded-full bg-black/10 dark:bg-white/10 animate-pulse" />
            ) : (
                <button
                    type="button"
                    role="switch"
                    aria-checked={isBattle}
                    aria-label="Toggle battle status"
                    disabled={updating}
                    onClick={onToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
                        isBattle ? "bg-[#3DD958]" : "bg-gray-300 dark:bg-gray-600"
                    }`}
                >
                    <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                            isBattle ? "translate-x-5" : "translate-x-0.5"
                        }`}
                    />
                </button>
            )}
        </div>

        <div className="mt-6 flex items-end justify-between gap-4">
            <p className="text-base text-gray-600 dark:text-gray-300">
                Battle is currently
            </p>
            {loading ? (
                <div className="h-8 w-16 rounded bg-black/10 dark:bg-white/10 animate-pulse" />
            ) : (
                <span
                    className={`rounded-full px-3 py-1 text-sm font-semibold ${
                        isBattle
                            ? "bg-[#3DD958] text-white"
                            : "bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-white"
                    }`}
                >
                    {updating ? "Updating..." : isBattle ? "ON" : "OFF"}
                </span>
            )}
        </div>
    </div>
);

// ── Page ───────────────────────────────────────────────────────────────────────
const MoreDashboard = () => {
    const [stats, setStats] = useState({});
    const [isBattle, setIsBattle] = useState(false);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    // showLoader=false => skeleton batavya vagar background ma refresh thase
    const fetchStats = useCallback(async (showLoader = true) => {
        try {
            if (showLoader) setLoading(true);
            const res = await getDashboardStats();
            if (res?.success) {
                const data = res.data || {};
                setStats(data);
                // isBattle data ma hoy ke res ma — banne handle
                setIsBattle(Boolean(data.isBattle ?? res.isBattle));
            } else {
            }
        } catch (err) {
        } finally {
            if (showLoader) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const handleToggleBattle = async () => {
        if (updating) return;

        const previous = isBattle;
        const next = !previous;

        try {
            setUpdating(true);
            setIsBattle(next); // optimistic update

            const res = await updateBattleStatus(next);

            if (res?.success) {

                // update pachi server thi latest data fari lavo (silent refresh)
                await fetchStats(false);
            } else {
                setIsBattle(previous); // rollback
            }
        } catch (err) {
            setIsBattle(previous); // rollback
        } finally {
            setUpdating(false);
        }
    };

    return (
        <div>
            <PageBreadcrumb pageTitle="Dashboard" />


            <div className="grid grid-cols-1 gap-x-5 gap-y-6 md:grid-cols-2">
                <div>
                    <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                        Battle Status
                    </h3>
                    <BattleStatusCard
                        isBattle={isBattle}
                        loading={loading}
                        updating={updating}
                        onToggle={handleToggleBattle}
                    />
                </div>

                {SECTIONS.map(({ title, key, ...card }) => (
                    <div key={key}>
                        <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                            {title}
                        </h3>
                        <StatCard {...card} value={stats[key]} loading={loading} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MoreDashboard;